import { db } from '../db/db.js';
import type { PantryKnowledge } from '../db/schema.js';
import { normalizeName } from './normalize.js';

/**
 * Canonical key for an item name (v0.4 alias normalization).
 *
 * 用 PantryKnowledge.id 當「同一物品」的 canonical key，這樣只要 alias 對到同一筆
 * knowledge，「全脂鮮乳」「鮮乳」「milk」都會被歸成同 key。沒對到 knowledge 才
 * fallback `normalizeName` 純 string 比對。
 *
 * 主要用在：
 *  - shopping.lookupLastUnitPrice — 上次買價跨 alias 對齊
 *  - shopping.markReceived 同名 dedup — 既有 active item 跨 alias 找
 *  - 未來建議補貨 / 重複檢查也走這裡
 */

interface KnowledgeIndex {
  byNormalizedAlias: Map<string, PantryKnowledge>;
  loadedAt: number;
}

let indexCache: KnowledgeIndex | null = null;
const INDEX_TTL_MS = 60_000;

async function getIndex(): Promise<KnowledgeIndex> {
  if (indexCache && Date.now() - indexCache.loadedAt < INDEX_TTL_MS) return indexCache;
  const all = await db.pantryKnowledge.toArray();
  const byNormalizedAlias = new Map<string, PantryKnowledge>();
  for (const k of all) {
    byNormalizedAlias.set(normalizeName(k.name), k);
    for (const a of k.aliases) {
      const key = normalizeName(a);
      if (!byNormalizedAlias.has(key)) byNormalizedAlias.set(key, k);
    }
  }
  indexCache = { byNormalizedAlias, loadedAt: Date.now() };
  return indexCache;
}

/** Test 與 seed 重灌時呼叫，強制下次重建 index。 */
export function clearCanonicalCache(): void {
  indexCache = null;
}

/**
 * 取 canonical key：對到 knowledge 回 `knowledge.id`；沒對到回 `normalizeName(name)`。
 *
 * 例：canonicalKey('全脂鮮乳') 若 fresh_milk aliases 含「全脂鮮乳」→ 'fresh_milk'
 *     canonicalKey('某私房調味') 沒對到 knowledge → normalizeName('某私房調味')
 */
export async function canonicalKey(name: string): Promise<string> {
  const target = normalizeName(name);
  if (!target) return '';
  const idx = await getIndex();
  const k = idx.byNormalizedAlias.get(target);
  return k ? k.id : target;
}

/**
 * 取「同一 canonical 對應的所有 normalizedName 集合」，包含 knowledge.name + aliases。
 *
 * 用於 `db.items.where('normalizedName').anyOf(...)` 跨 alias 查詢。沒對到 knowledge 時
 * 回單元素 set（即 normalizedName 自己），保持原本 exact-match 行為。
 */
export async function knowledgeAliasNormalizedSet(name: string): Promise<Set<string>> {
  const target = normalizeName(name);
  if (!target) return new Set();
  const idx = await getIndex();
  const k = idx.byNormalizedAlias.get(target);
  if (!k) return new Set([target]);
  const variants = new Set<string>([normalizeName(k.name)]);
  for (const a of k.aliases) variants.add(normalizeName(a));
  return variants;
}
